import type {
  DraftState,
  FileIdentity,
  WizardFile,
  WizardReleasedFile,
  WizardRemovedGroup,
  WizardRemoval,
  WizardSampleGroup
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { FileForProcessing } from '../../Model/Sample/ISample.ts'
import { sampleNameOf } from './sampleLabels.ts'
import { currentPools, placedGroups } from './pools.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'

const fileKeysOfGroup = (group: WizardSampleGroup): string[] => [group.forward, ...(group.reverse ? [group.reverse] : [])]

const serverFileIdsOf = (draft: DraftState, group: WizardSampleGroup): string[] =>
  fileKeysOfGroup(group).flatMap((key) => draft.files[key]?.upload?.serverFileId ?? [])

const itemIdsOf = (group: WizardSampleGroup): string[] => [
  ...new Set([group.item.itemId, ...Object.values(group.archiveItems ?? {}).map((item) => item.itemId)].filter((id): id is string => !!id))
]

/* a waiting group has never been sent; a failed creation may still have made the item before the error */
const mayBeOnServer = (group: WizardSampleGroup) =>
  group.item.status === 'created' || group.item.status === 'creating' || group.item.status === 'failed' || itemIdsOf(group).length > 0

/**
 * The draft without the groups and their files. A group whose item may be on the server leaves a
 * pending removal behind for the write queue; one that never got that far is simply gone.
 */
export const withGroupsRemoved = (draft: DraftState, groupKeys: readonly string[]): DraftState => {
  const groups = { ...draft.groups }
  const files = { ...draft.files }
  const removals = { ...draft.removals }
  const gone = new Set<string>()
  const removedNames = new Set(draft.removedNames)
  const released = [...(draft.releasedFiles ?? [])]
  const removedGroups = { ...draft.removedGroups }
  for (const key of groupKeys) {
    const group = groups[key]
    if (!group) {
      continue
    }
    removedGroups[key] = snapshotOf(draft, group)
    for (const name of [sampleNameOf(draft, group), draft.files[group.forward]?.name]) {
      if (name) {
        removedNames.add(name)
      }
    }
    for (const fileKey of fileKeysOfGroup(group)) {
      const file = draft.files[fileKey]
      const serverFileId = file?.upload?.serverFileId
      if (draft.orderId && serverFileId && !released.some((entry) => entry.serverFileId === serverFileId)) {
        released.push({ name: file.name, ...(file.fromServer ? {} : { size: file.size }), serverFileId })
      }
    }
    if (draft.orderId && group.item.status !== 'removed' && mayBeOnServer(group)) {
      removals[key] = {
        groupKey: key,
        name: sampleNameOf(draft, group) ?? draft.files[group.forward]?.name ?? key,
        /* a group dropped back keeps its key: an earlier removal not yet done is folded in */
        itemIds: [...new Set([...(removals[key]?.itemIds ?? []), ...itemIdsOf(group)])],
        serverFileId: draft.files[group.forward]?.upload?.serverFileId,
        serverFileIds: [...new Set([...(removals[key]?.serverFileIds ?? []), ...serverFileIdsOf(draft, group)])],
        status: 'pending'
      }
    }
    delete groups[key]
    for (const fileKey of fileKeysOfGroup(group)) {
      delete files[fileKey]
      gone.add(fileKey)
    }
  }
  const removed: DraftState = {
    ...draft,
    groups,
    files,
    fileOrder: draft.fileOrder.filter((key) => !gone.has(key)),
    removals: Object.keys(removals).length > 0 ? removals : undefined,
    removedNames: removedNames.size > 0 ? [...removedNames] : undefined,
    releasedFiles: released.length > 0 ? released : undefined,
    removedGroups: Object.keys(removedGroups).length > 0 ? removedGroups : undefined
  }
  return withFirstPoolFilled(removed)
}

const snapshotOf = (draft: DraftState, group: WizardSampleGroup): WizardRemovedGroup => {
  const { key, forward, reverse, itemType, typeSource, pool, run, manualPrimers, archiveOverrides } = group
  return {
    name: sampleNameOf(draft, group) ?? draft.files[forward]?.name ?? key,
    files: fileKeysOfGroup(group).flatMap((fileKey) => (draft.files[fileKey] ? [draft.files[fileKey]] : [])),
    group: { key, forward, reverse, itemType, typeSource, pool, run, manualPrimers, archiveOverrides }
  }
}

const releasedIdOf = (draft: DraftState, file: FileIdentity) => releasedFileFor(draft.releasedFiles, file)

/** Whether every file of the removed group is still on the order, so it comes back without being dropped again. */
export const canReadd = (draft: DraftState, removed: WizardRemovedGroup): boolean =>
  !!draft.orderId && removed.files.length > 0 && removed.files.every((file) => !!releasedIdOf(draft, file))

/** The removed group whose forward file `file` is again, by name and size. */
export const removedGroupOf = (draft: DraftState, file: FileIdentity): [string, WizardRemovedGroup] | undefined =>
  Object.entries(draft.removedGroups ?? {}).find(([, removed]) => {
    const forward = removed.files.find((candidate) => candidate.key === removed.group.forward)
    return !!forward && forward.name === file.name && (!!forward.fromServer || forward.size === file.size)
  })

/* a pool index that no longer holds any item has moved or gone; the first pool is the safe place */
const poolStillThere = (draft: DraftState, pool: number | undefined) =>
  pool && Object.values(draft.groups).some((group) => (group.pool ?? 0) === pool) ? pool : undefined

/** The lab's choices for a group coming back, taken from what it was when removed. */
export const restoredChoices = (draft: DraftState, removed: WizardRemovedGroup): Partial<WizardSampleGroup> => {
  const { itemType, typeSource, pool, manualPrimers, archiveOverrides } = removed.group
  return {
    ...(itemType ? { itemType } : {}),
    ...(typeSource ? { typeSource } : {}),
    ...(poolStillThere(draft, pool) ? { pool } : {}),
    ...(manualPrimers ? { manualPrimers } : {}),
    ...(archiveOverrides ? { archiveOverrides } : {})
  }
}

/** The draft without the record of a removed group, and its names no longer expected to match nothing. */
export const withoutRemovedRecord = (draft: DraftState, groupKey: string): DraftState => {
  const removed = draft.removedGroups?.[groupKey]
  if (!removed) {
    return draft
  }
  const names = new Set([removed.name, ...removed.files.map((file) => file.name)])
  const removedNames = (draft.removedNames ?? []).filter((name) => !names.has(name))
  const removedGroups = { ...draft.removedGroups }
  delete removedGroups[groupKey]
  return {
    ...draft,
    removedNames: removedNames.length > 0 ? removedNames : undefined,
    removedGroups: Object.keys(removedGroups).length > 0 ? removedGroups : undefined
  }
}

/**
 * Removed groups back in the draft from the files the order kept: no upload, no file handle. The item is
 * created afresh once the old one is removed; a removal that failed is tried again.
 */
export const withGroupsReadded = (draft: DraftState, groupKeys: readonly string[]): DraftState =>
  groupKeys.reduce((current, key) => {
    const removed = current.removedGroups?.[key]
    if (!removed || key in current.groups || !canReadd(current, removed)) {
      return current
    }
    const serverIds = new Set<string>()
    const files: Record<string, WizardFile> = { ...current.files }
    const fileOrder = [...current.fileOrder]
    for (const file of removed.files) {
      const serverFileId = releasedIdOf(current, file) as string
      serverIds.add(serverFileId)
      files[file.key] = { ...file, rejection: undefined, upload: { status: 'uploaded', progress: 1, serverFileId } }
      if (!fileOrder.includes(file.key)) {
        fileOrder.push(file.key)
      }
    }
    const { key: groupKey, forward, reverse, run } = removed.group
    const restored = (key: string, mate?: string): WizardSampleGroup => ({
      key,
      forward: key,
      ...(mate ? { reverse: mate } : {}),
      ...(run !== undefined ? { run } : {}),
      ...restoredChoices(current, removed),
      item: { status: 'waiting' }
    })
    /* a pair removed so the order could switch to merged reads comes back as two of them */
    const merged = !!current.settings && SampleConfigurationTypes[current.settings.sampleConfiguration.value].impliesMerged
    const back =
      merged && reverse
        ? { [groupKey]: { ...restored(forward), key: groupKey }, [reverse]: restored(reverse) }
        : { [groupKey]: { ...restored(forward, reverse), key: groupKey } }
    const releasedFiles = (current.releasedFiles ?? []).filter((entry) => !serverIds.has(entry.serverFileId))
    return withoutRemovedRecord(
      {
        ...current,
        files,
        fileOrder,
        groups: { ...current.groups, ...back },
        releasedFiles: releasedFiles.length > 0 ? releasedFiles : undefined,
        removals: withRemovalsRetried(current.removals, [...serverIds])
      },
      key
    )
  }, draft)

/* files are uploaded to the first pool, so it can never be deleted: when a removal empties it, the later pools move up instead */
const withFirstPoolFilled = (draft: DraftState): DraftState => {
  const placed = placedGroups(draft)
  if (placed.length === 0 || placed.some((group) => (group.pool ?? 0) === 0)) {
    return draft
  }
  const pools = currentPools(draft)
  return {
    ...draft,
    groups: Object.fromEntries(
      Object.entries(draft.groups).map(([key, group]) => [key, key in pools ? { ...group, pool: pools[key] || undefined } : group])
    )
  }
}

/** The server id of a kept file that `file` is again, by name and size; by name alone when the size is unknown. */
export const releasedFileFor = (released: readonly WizardReleasedFile[] | undefined, file: FileIdentity): string | undefined =>
  released?.find((entry) => entry.name === file.name && (entry.size === undefined || entry.size === file.size))?.serverFileId

export const unresolvedRemovals = (draft: DraftState): WizardRemoval[] => Object.values(draft.removals ?? {})

export const failedRemovals = (draft: DraftState): WizardRemoval[] =>
  unresolvedRemovals(draft).filter((removal) => removal.status === 'failed')

export const pendingRemovals = (draft: DraftState): WizardRemoval[] =>
  unresolvedRemovals(draft).filter((removal) => removal.status === 'pending')

const filesOfRemoval = (removal: WizardRemoval): string[] => removal.serverFileIds ?? (removal.serverFileId ? [removal.serverFileId] : [])

export const removalHolds = (removal: WizardRemoval, fileIds: readonly (string | undefined)[]): boolean =>
  filesOfRemoval(removal).some((fileId) => fileIds.includes(fileId))

/**
 * The removal whose old item may still hold one of the files: outside combined sample pairs the server refuses
 * an item on an upload another live item holds, so a new item on them waits until the removal is done.
 */
export const removalHolding = (draft: DraftState, fileIds: readonly (string | undefined)[]): WizardRemoval | undefined =>
  unresolvedRemovals(draft).find((removal) => removalHolds(removal, fileIds))

export const removalHoldingGroup = (draft: DraftState, group: WizardSampleGroup): WizardRemoval | undefined =>
  removalHolding(draft, serverFileIdsOf(draft, group))

/** Failed removals holding one of the files, pending again: a sample coming back on them would otherwise wait on them for good. */
export const withRemovalsRetried = (removals: DraftState['removals'], fileIds: readonly string[]): DraftState['removals'] => {
  const retried = Object.values(removals ?? {}).filter((removal) => removal.status === 'failed' && removalHolds(removal, fileIds))
  if (!removals || retried.length === 0) {
    return removals
  }
  return {
    ...removals,
    ...Object.fromEntries(retried.map((removal) => [removal.groupKey, { ...removal, status: 'pending' as const, error: undefined }]))
  }
}

export type LocatedItem = { item: FileForProcessing; poolId: string }

/**
 * The server items a removal deletes: those it knew, and any made from its forward file that no group of
 * the draft claims (an archive's further items, an item whose creation answer was lost).
 */
export const removalTargets = (removal: WizardRemoval, located: readonly LocatedItem[], draft: DraftState): LocatedItem[] => {
  const claimed = new Set(Object.values(draft.groups).flatMap(itemIdsOf))
  return located.filter(
    ({ item }) =>
      !claimed.has(item.id) &&
      (removal.itemIds.includes(item.id) || (!!removal.serverFileId && item.forwardFileId === removal.serverFileId))
  )
}
